/* eslint-disable @typescript-eslint/member-ordering */
import {
  AsyncPipe,
  NgFor,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet,
  ViewportScroller,
} from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { CUSTOM_ICON_TYPE, CustomPaginationModel } from '@shared/models';
import { DeviceService } from '@shared/services';
import {
  IconModule,
  PaginationBuilder,
  PaginationConfig,
  PaginationItem,
  PaginationItemType,
} from '@spartacus/storefront';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

const RANGE_COUNT_MOBILE = 3;
const RANGE_COUNT_DESKTOP = 7;

@Component({
  selector: 'sd-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  imports: [NgFor, AsyncPipe, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet, RouterLink, IconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdvorCustomPaginationComponent implements OnInit {
  /** The (optional) pageRoute used for the anchor links created in the pagination   */
  @Input() pageRoute: string;

  /** The (optional) query parameter which is added to the page route.  */
  @Input() queryParam: string;

  /**
   * Whenever there's a default page specified, the routing logic
   * will omit the page number in routeLink or parameters.
   */
  @Input() defaultPage: unknown;

  @Output() viewPageEvent: EventEmitter<number> = new EventEmitter<number>();

  pages$: Observable<PaginationItem[]>;
  itemTypes = PaginationItemType;
  iconType = CUSTOM_ICON_TYPE;

  get pagination(): CustomPaginationModel {
    return this.pagination$.getValue();
  }

  @Input() set pagination(value: CustomPaginationModel) {
    const pagination = Object.assign({}, value);

    if ('page' in pagination) {
      pagination.currentPage = pagination.page;
    }

    this.pagination$.next(pagination);
  }

  private pagination$ = new BehaviorSubject<CustomPaginationModel>({});

  constructor(
    private paginationBuilder: PaginationBuilder,
    private activatedRoute: ActivatedRoute,
    private paginationConfig: PaginationConfig,
    private deviceService: DeviceService,
    private viewportScroller: ViewportScroller,
  ) {}

  ngOnInit(): void {
    this.pages$ = combineLatest({ pagination: this.pagination$, isMobile: this.deviceService.isMobileSmDown$ }).pipe(
      map(({ pagination, isMobile }) => {
        if (!this.paginationConfig.pagination) return [];

        this.paginationConfig.pagination.rangeCount = isMobile ? RANGE_COUNT_MOBILE : RANGE_COUNT_DESKTOP;

        return this.paginationBuilder.paginate(pagination?.totalPages || 0, pagination?.currentPage || 0);
      }),
    );
  }

  pageChange(page: PaginationItem): void {
    if (page.number === undefined) return;
    if (page.number >= 0) {
      this.viewPageEvent.emit(page.number);
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  /**
   * Indicates whether the given item is the current item.
   *
   * @param item PaginationItem
   * @returns boolean
   */
  isCurrent(item: PaginationItem): boolean {
    return item.type === PaginationItemType.PAGE && item.number === this.pagination.currentPage;
  }

  /**
   * Indicates whether the pagination item is inactive. This is used
   * to disabled a link or set the tabindex to `-1`.
   *
   * Defaults to true
   *
   * @param item PaginationItem
   * @returns returns -1 in case of a disabled
   */
  isInactive(item: PaginationItem): boolean {
    return !Object.prototype.hasOwnProperty.call(item, 'number') || item.number === this.pagination.currentPage;
  }

  getQueryParams(item: PaginationItem): Params {
    const queryParams = Object.assign({}, this.activatedRoute.snapshot.queryParams);
    if (item.number !== undefined && this.pagination.totalPages !== undefined) {
      if (this.queryParam && item.number < this.pagination.totalPages && !this.isCurrent(item)) {
        queryParams[this.queryParam] = item.number;
      }
    }

    const isParameterInCurrentUrl = this.queryParam in this.activatedRoute.snapshot.queryParams;
    if (queryParams[this.queryParam] === this.defaultPage && !isParameterInCurrentUrl) {
      delete queryParams[this.queryParam];
    }

    return queryParams;
  }
}
